// Tour Management Page - Replaces ToursManagementPage
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Tooltip,
  CircularProgress,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Route as TourIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';

import { useTours, useDeleteTour } from '../modules/tour/hooks/useTours';
import { Tour } from '../modules/tour/types';
import { useAppSelector } from '../store/hooks';
import { selectSelectedMuseum } from '../store/slices/museumSlice';

const TourTable: React.FC<{
  tours: Tour[];
  canEdit: boolean;
  onView: (id: number) => void;
  onEdit: (id: number) => void;
  onDelete: (tour: Tour) => void;
}> = ({ tours, canEdit, onView, onEdit, onDelete }) => (
  <TableContainer component={Paper}>
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>Name</TableCell>
          <TableCell>Description</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Waypoints</TableCell>
          <TableCell align="center">Actions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {tours.map(tour => (
          <TableRow key={tour.id} hover>
            <TableCell>
              <Typography variant="subtitle2">Tour {tour.tour_id}</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2" sx={{ maxWidth: 200 }}>
                {tour.description || 'No description'}
              </Typography>
            </TableCell>
            <TableCell>
              <Chip
                label={tour.is_active ? 'Active' : 'Inactive'}
                color={tour.is_active ? 'success' : 'default'}
                size="small"
              />
            </TableCell>
            <TableCell>
              <Chip label={`${tour.waypoint_lists?.length || 0} waypoints`} color="primary" size="small" />
            </TableCell>
            <TableCell align="center">
              <Tooltip title="View Details">
                <IconButton size="small" onClick={() => onView(tour.id)}>
                  <ViewIcon />
                </IconButton>
              </Tooltip>
              {canEdit && (
                <>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => onEdit(tour.id)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => onDelete(tour)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const TourManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const selectedMuseum = useAppSelector(selectSelectedMuseum);
  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  // Dialog states
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedTour, setSelectedTour] = useState<Tour | null>(null);
  // Data hooks - fetch for selected museum or all museums
  const {
    data: tours,
    loading: toursLoading,
    refetch: refetchTours,
  } = useTours({
    museumCode: selectedMuseum?.museum_code,
  });
  // Show loading if museum list not yet loaded (can optimize if loading gets exposed)
  const isLoading = toursLoading && !tours;
  // Get current user from Redux store
  const { user } = useAppSelector(state => state.auth);
  const isAdmin = user?.role === 'admin';
  const canEdit = isAdmin; // Only Admin can edit, Super User has same permissions as User
  // Tour actions
  const handleViewTour = (tourId: number) => {
    navigate(`/tours/${tourId}`);
  };
  const handleEditTour = (tourId: number) => {
    navigate(`/tours/${tourId}/edit`);
  };
  // Delete tour
  const deleteTour = useDeleteTour();
  const handleDeleteTour = async () => {
    if (!selectedTour) return;
    try {
      await deleteTour.mutate(selectedTour.id);
      setSnackbarMessage('Tour deleted successfully');
      setSnackbarSeverity('success');
      await refetchTours(); // Refresh the list
      setOpenDeleteDialog(false);
      setSelectedTour(null);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to delete tour';
      setSnackbarMessage(message);
      setSnackbarSeverity('error');
    }
    setSnackbarOpen(true);
  };

  const filteredTours = tours || [];

  // Museum tab for "All Museums" mode
  const [activeMuseumTab, setActiveMuseumTab] = useState('all');

  useEffect(() => {
    setActiveMuseumTab('all');
  }, [selectedMuseum]);

  // Group tours by museum when All Museums is selected
  const groupedTours = !selectedMuseum
    ? filteredTours.reduce<Record<string, Tour[]>>((acc, tour) => {
        const key = tour.museum_code || 'Unknown';
        if (!acc[key]) acc[key] = [];
        acc[key].push(tour);
        return acc;
      }, {})
    : null;

  const museumGroups = groupedTours ? Object.entries(groupedTours).sort(([a], [b]) => a.localeCompare(b)) : null;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4">
            <TourIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Tour Management
          </Typography>
          {selectedMuseum && (
            <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>
              {selectedMuseum.name} Tours
            </Typography>
          )}
          {!selectedMuseum && (
            <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>
              All Museums
            </Typography>
          )}
        </Box>
        {canEdit && selectedMuseum && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/tours/create')}>
              Create Tour
            </Button>
          </Box>
        )}
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : selectedMuseum ? (
        /* Single museum view */
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Tours ({filteredTours.length})
            </Typography>
            {filteredTours.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary">
                  No tours found for {selectedMuseum.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Create your first tour for this museum
                </Typography>
                {canEdit && (
                  <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/tours/create')}>
                    Create Tour
                  </Button>
                )}
              </Box>
            ) : (
              <TourTable
                tours={filteredTours}
                canEdit={canEdit}
                onView={handleViewTour}
                onEdit={handleEditTour}
                onDelete={tour => {
                  setSelectedTour(tour);
                  setOpenDeleteDialog(true);
                }}
              />
            )}
          </CardContent>
        </Card>
      ) : (
        /* All Museums view - tabs to toggle between museums */
        <>
          {museumGroups && museumGroups.length === 0 ? (
            <Card>
              <CardContent>
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="h6" color="text.secondary">
                    No tours found
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Museum toggle tabs */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs
                  value={activeMuseumTab}
                  onChange={(_e, val) => setActiveMuseumTab(val)}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab label="All" value="all" />
                  {museumGroups &&
                    museumGroups.map(([museumCode, museumTours]) => (
                      <Tab key={museumCode} label={`${museumCode} (${museumTours.length})`} value={museumCode} />
                    ))}
                </Tabs>
              </Box>

              {museumGroups &&
                museumGroups
                  .filter(([museumCode]) => activeMuseumTab === 'all' || museumCode === activeMuseumTab)
                  .map(([museumCode, museumTours], index) => (
                    <Box key={museumCode}>
                      {index > 0 && <Divider sx={{ my: 3 }} />}
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            {museumCode} — Tours ({museumTours.length})
                          </Typography>
                          <TourTable
                            tours={museumTours}
                            canEdit={canEdit}
                            onView={handleViewTour}
                            onEdit={handleEditTour}
                            onDelete={tour => {
                              setSelectedTour(tour);
                              setOpenDeleteDialog(true);
                            }}
                          />
                        </CardContent>
                      </Card>
                    </Box>
                  ))}
            </>
          )}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete Tour {selectedTour?.tour_id}? This action cannot be undone and will remove
            all associated waypoints and content.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteTour} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TourManagementPage;
